/**
 * CPAYMENT-1742: одностраничный экран оплаты — перевод и чек на одной странице.
 *
 * Экран не склеивает два прежних экрана подряд, а раскладывает их в одну последовательность из
 * двух пронумерованных шагов: «переведите» → «приложите чек». Нумерация здесь несёт смысл (это
 * настоящий порядок действий), поэтому иерархия строится на ней, а не на цветных плашках:
 *
 *  - шаг 1 отдаёт реквизит и сумму одним привычным блоком с копированием, под ним банк и
 *    инструкция; инструкция короткая — всё, что дальше, уже нарисовано на самом экране;
 *  - шаг 2 — спокойный блок загрузки вместо аварийной жёлтой карточки: требование чека сказано
 *    один раз в подзаголовке, а не криком;
 *  - внизу привычная пара кнопок «Вернуться» / «Я оплатил».
 *
 * Если чек не требуется (`simpleConfirmation !== false`), второй шаг не рисуется вовсе — вместе с
 * ним уходит и нумерация первого: остаётся обычный экран реквизитов, где «Я оплатил» подтверждает
 * оплату сразу.
 */
import { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { Transaction } from '../../../services/types';
import { getAzPhoneNumberMask, getCardNumberMask, getPhoneNumberMask } from '../../../services/utils';
import m10Logo from '../../../assets/images/m10.svg';
import { track, buildBasePayload } from '../../../analytics';
import QR from '../../../components/atoms/QR/QR';
import QrPayBlock from '../../../components/organisms/QrPayBlock/QrPayBlock';
import useIsDesktop from '../../../hooks/useIsDesktop';
import { useReceiptPicker } from '../../../hooks/useReceiptPicker';
import { useReturnedToTab } from '../useReturnedToTab';
import { cameForReceipt, receiptEntryUrl } from '../receiptEntry';
import {
  getReceiptInstructionsKey,
} from '../../../services/receiptUploadPolicy';
import UploadNotice from '../../../components/UploadNotice/UploadNotice';
import {
  confirmErrorTextKey,
  ReceiptUploadHandlers,
  ReceiptUploadResult,
} from '../../../services/receiptUpload';
import {
  GateScreen,
  GateLeaveModal,
  GateSpinnerRing,
  GATE,
  GateButtonStack,
  GatePrimaryButton,
  GateQuietButton,
} from '../components';
import GateAmountAlert from '../components/GateAmountAlert';
import { showsAmountChangeNotice } from '../../../services/amountChange';
import { BankLogo, hasBankLogo } from '../../../utils/bankLogos';
import { copyToClipboard } from '../../../services/clipboard';
import { CopiedField } from '../../../analytics/events';
import type { CancelReasonOption, DeclineContext } from '../../../services/types';

// ─── Icons ────────────────────────────────────────────────────────────────────
const SbpIcon = () => (
  <svg width="100" height="50" viewBox="0 0 100 50" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ height: 22, width: 'auto', flexShrink: 0 }}>
    <path d="M0 10.8838L6.05499 21.7067V28.3084L0.00708333 39.11L0 10.8838Z" fill="#5B57A2"/>
    <path d="M23.2487 17.7676L28.9224 14.2901L40.5341 14.2793L23.2487 24.8685V17.7676Z" fill="#D90751"/>
    <path d="M23.2166 10.8196L23.2487 25.1487L17.1795 21.4196V0L23.217 10.8196H23.2166Z" fill="#FAB718"/>
    <path d="M40.5341 14.2796L28.922 14.2904L23.2166 10.8196L17.1795 0L40.5337 14.2796H40.5341Z" fill="#ED6F26"/>
    <path d="M23.2487 39.17V32.2179L17.1795 28.5596L17.1829 50.0004L23.2487 39.17Z" fill="#63B22F"/>
    <path d="M28.9083 35.7242L6.05458 21.7067L0 10.8838L40.5095 35.71L28.9079 35.7242H28.9083Z" fill="#1487C9"/>
    <path d="M17.1834 50.0004L23.2484 39.17L28.908 35.7241L40.5092 35.71L17.1834 50.0004Z" fill="#017F36"/>
    <path d="M0.0071106 39.1093L17.2292 28.5593L11.4392 25.0068L6.05502 28.3077L0.0071106 39.1093Z" fill="#984995"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M66.2021 26.6991C70.1096 26.6991 73.0112 24.3033 73.0112 20.6713C73.0112 17.1557 70.8705 14.8734 67.2924 14.8734C65.6412 14.8734 64.2787 15.4544 63.2525 16.4574C63.4974 14.3847 65.2504 12.871 67.1803 12.871C67.6259 12.871 70.9791 12.8641 70.9791 12.8641L72.8755 9.23535C72.8755 9.23535 68.6655 9.33138 66.7083 9.33138C62.2367 9.4091 59.2162 13.4736 59.2162 18.4101C59.2162 24.1617 62.162 26.6991 66.2021 26.6991ZM66.2259 18.0875C67.6767 18.0875 68.6824 19.0408 68.6824 20.6709C68.6824 22.1378 67.7888 23.3467 66.2259 23.3499C64.7308 23.3499 63.7249 22.2297 63.7249 20.6957C63.7249 19.0653 64.7308 18.0875 66.2259 18.0875ZM88.0995 14.038V26.2621H83.7344V17.6871H79.5316V26.2621H75.1665V14.0372L88.0995 14.038ZM53.0928 22.8763C54.6325 22.764 55.6632 22.1695 55.6632 22.1695L57.2786 25.1582C56.244 25.8227 54.5466 26.2548 53.3162 26.3004C48.9141 26.3813 45.852 23.7027 45.852 19.8001C45.852 15.9468 49.1098 13.2043 53.3162 13.2043C54.9828 13.2043 56.4157 13.6514 57.4916 14.4669C57.2651 14.8681 56.9573 15.4239 56.6648 15.9557L56.1962 16.8094L55.77 17.589C55.77 17.589 54.2199 16.7768 53.0482 16.7768C51.1374 16.7768 49.7458 17.8873 49.7458 19.8245C49.7458 21.811 51.3228 22.9288 53.0928 22.8763Z" fill="black"/>
  </svg>
);

const CardIcon = () => (
  <svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <rect x="1" y="1" width="22" height="16" rx="3" stroke="#333" strokeWidth="1.5" fill="none"/>
    <rect x="1" y="5" width="22" height="3" fill="#333"/>
    <rect x="4" y="11" width="6" height="2" rx="1" fill="#999"/>
  </svg>
);

const QrIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <rect x="2" y="2" width="8" height="8" rx="1.5" stroke="#333" strokeWidth="1.5"/>
    <rect x="14" y="2" width="8" height="8" rx="1.5" stroke="#333" strokeWidth="1.5"/>
    <rect x="2" y="14" width="8" height="8" rx="1.5" stroke="#333" strokeWidth="1.5"/>
    <rect x="5" y="5" width="2" height="2" fill="#333"/>
    <rect x="17" y="5" width="2" height="2" fill="#333"/>
    <rect x="5" y="17" width="2" height="2" fill="#333"/>
    <rect x="14" y="14" width="3" height="3" fill="#333"/>
    <rect x="19" y="19" width="3" height="3" fill="#333"/>
  </svg>
);

// Фирменный знак m10 — такой же цветной логотип бренда, как знак СБП рядом: клиент узнаёт
// приложение, в котором будет платить, раньше, чем читает заголовок.
const M10Icon = () => (
  <img src={m10Logo} alt='m10' style={{ height: 22, width: 22, flexShrink: 0 }} />
);

const ClipIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path d="M15.75 8.25v8.25a3.75 3.75 0 1 1-7.5 0V6.75a2.25 2.25 0 1 1 4.5 0v9a.75.75 0 0 1-1.5 0V8.25"
      stroke={GATE.action} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const RemoveIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.66 17.66L6.34 6.34" stroke="#8a8a8a" strokeWidth="2.6" strokeLinecap="round"/>
    <path d="M6.34 17.66L17.66 6.34" stroke="#8a8a8a" strokeWidth="2.6" strokeLinecap="round"/>
  </svg>
);

const PhoneIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <rect x="6" y="2" width="12" height="20" rx="3" stroke={GATE.action} strokeWidth="1.6"/>
    <path d="M10.5 18.5h3" stroke={GATE.action} strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/** Знак валюты для суммы. Незнакомая валюта показывается своим кодом — это лучше пустоты. */
const CURRENCY_SIGNS: Record<string, string> = {
  RUB: '₽',
  KZT: '₸',
  UAH: '₴',
  KRW: '₩',
  USD: '$',
  EUR: '€',
  AZN: '₼',
};

// ─── Секции-шаги ──────────────────────────────────────────────────────────────
const Section = styled.section`
  text-align: left;
  margin-top: 22px;

  /* Под шапкой уже стоит свой отступ: полный зазор здесь удваивал бы расстояние до первого блока.
     Промежуток между блоками при этом остаётся прежним. */
  &:first-of-type {
    margin-top: 3px;
  }
`;

const MethodMark = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  align-self: center;
  margin-left: 4px;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
`;

const SectionNum = styled.span<{ $done?: boolean }>`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${GATE.sans};
  font-size: 12px;
  font-weight: 700;
  color: ${({ $done }) => ($done ? '#fff' : GATE.actionText)};
  background: ${({ $done }) => ($done ? GATE.action : GATE.actionSoft)};
  transition: background 0.2s, color 0.2s;
`;

// Заголовки обоих шагов одного размера: это два равноправных действия одного экрана. Заголовок
// шага чека при этом сразу говорит, чем грозит пропуск, а не просто называет шаг.
const StepTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-family: ${GATE.sans};
  /* Заголовок шага называет действие и не должен перетягивать взгляд с данных под ним: номер и сумма
     крупнее и жирнее его. */
  font-size: 15px;
  font-weight: 600;
  color: ${GATE.ink};
  line-height: 1.35;
`;

// Тело шага всегда во всю ширину, без отступа под номер: на экранах шире узкого телефона такой
// отступ сдвигал реквизиты, сумму и загрузку чека вправо от заголовка, и экран выглядел перекошенным.
const SectionBody = styled.div``;

// Тип перевода без нумерации: когда шаг всего один, он и есть весь экран.
const TypeBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 4px 12px;
  font-family: 'PT Mono', monospace;
  font-size: 14px;
  color: #333;
  text-align: left;
`;

const RequisiteValue = styled.div`
  margin: 2px 0 0;
  font-family: ${GATE.mono};
  font-size: 18px;
  line-height: 1.3;
  font-weight: 700;
  color: ${GATE.ink};
  white-space: nowrap;
`;

const AmountValue = styled.div`
  margin: 2px 0 0;
  font-family: ${GATE.mono};
  font-size: 26px;
  line-height: 1.2;
  font-weight: 700;
  color: ${GATE.ink};
  word-break: break-word;
`;

/**
 * Знак валюты рисуется не моноширинным шрифтом страницы: в PT Mono глиф маната (U+20BC) совпадает
 * с рублёвым, и депозит в манатах подписывался рублём. Начертание легче цифр — знак не должен
 * перебивать саму сумму.
 */
const CurrencyMark = styled.span`
  font-family: ${GATE.sans};
  font-weight: 500;
`;

const AmountCaption = styled.div`
  margin-top: 2px;
  font-family: ${GATE.sans};
  font-size: 12px;
  color: ${GATE.muted};
`;

// Кнопка копирования подписана словами и словами же отчитывается: иконка молчит и об успехе, и
// о провале, а внутри iframe копирование может быть запрещено настройкой мерчанта.
const CopyTextBtn = styled.button<{ $copied: boolean }>`
  margin-top: 10px;
  /* Обе кнопки одной ширины, а не по тексту: стоят друг под другом и должны читаться парой. */
  width: 240px;
  max-width: 100%;
  /* Высота под палец: при более плоской кнопке на телефоне промахиваются. */
  min-height: 38px;
  box-sizing: border-box;
  padding: 8px 14px;
  border: 1px solid ${({ $copied }) => ($copied ? GATE.action : 'rgba(106, 170, 58, 0.45)')};
  border-radius: 10px;
  background: ${({ $copied }) => ($copied ? GATE.action : '#fff')};
  color: ${({ $copied }) => ($copied ? '#fff' : GATE.actionText)};
  font-family: ${GATE.sans};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
  &:active { transform: scale(0.98); }
`;

// ─── Загрузка чека ────────────────────────────────────────────────────────────
// Дропзоны здесь нет намеренно: на платёжной странице клиент чаще всего с телефона, а рамка
// «перетащите файл» обещала бы способ, которым он всё равно не воспользуется.
const FileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
  padding: 12px 14px;
  border-radius: 14px;
  background: ${GATE.surface};
  border: 1px solid ${GATE.line};
  animation: ${fadeIn} 0.2s ease;
`;

const FileName = styled.span`
  flex: 1;
  min-width: 0;
  font-family: ${GATE.mono};
  font-size: 13px;
  color: ${GATE.ink};
  word-break: break-all;
  text-align: left;
`;

const IconBtn = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  &:disabled { cursor: not-allowed; opacity: 0.4; }
`;

const PhoneToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 12px auto 0;
  padding: 4px 2px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: ${GATE.sans};
  font-size: 13px;
  color: ${GATE.actionText};
`;

const QrPanel = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 8px;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid ${GATE.line};
  background: #fff;
  animation: ${fadeIn} 0.2s ease;
`;

const QrFrame = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 4px;
  display: flex;
  flex-shrink: 0;
`;

const QrText = styled.div`
  font-family: ${GATE.sans};
  font-size: 13px;
  color: ${GATE.muted};
  line-height: 1.5;
  text-align: left;
`;

// ─── Завершение ───────────────────────────────────────────────────────────────
const Percent = styled.span`
  font-family: 'PT Mono', monospace;
  font-size: 13px;
`;

// ─── Component ────────────────────────────────────────────────────────────────
interface Props {
  transaction: Transaction;
  uuid: string;
  timeLeft: string;
  onCancel: (reason?: CancelReasonOption) => void;
  /** Cancel as a refusal of what this screen offers. */
  onDeclineOffer: (context: DeclineContext) => Promise<void> | void;
  /** Cancel survey items, in order; empty — no survey. */
  cancelReasons?: CancelReasonOption[];
  /** CPAYMENT-1730: уход к мерчанту без отмены заявки. */
  onLeave?: () => void;
  /** CPAYMENT-1730: клиент уже отметил, что платит — выход спрашивает, что именно он хочет. */
  receiptIntent?: boolean;
  /** Финальное подтверждение оплаты: чек уже на бэкенде (или не требуется). */
  onConfirm: () => Promise<void>;
  /** With a mandatory receipt: the receipt and the confirmation in one request. */
  onConfirmWithReceipt: (file: File, handlers: ReceiptUploadHandlers) => Promise<ReceiptUploadResult>;
}

// ─── Экран оплаты: одна система вместо набора самостоятельных блоков ─────────
// Порядок экрана — порядок действий клиента: куда и сколько перевести → как это сделать → чек →
// подтверждение. Всё справочное стоит после данных и тихо, чтобы не разрывать этот путь. Залитая
// кнопка на экране одна — «Я оплатил»; загрузка чека выглядит как место для файла, а не как ещё
// одна главная кнопка.

const DataCard = styled.div`
  margin-top: 4px;
  padding: 14px 16px 16px;
  border-radius: 16px;
  background: ${GATE.surface};
  text-align: center;
`;

const DataLabel = styled.div`
  font-family: ${GATE.sans};
  font-size: 12px;
  color: ${GATE.muted};
`;

const DataDivider = styled.div`
  height: 1px;
  margin: 14px 0;
  background: ${GATE.line};
`;

const BankRow = styled.div`
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: ${GATE.sans};
  font-size: 14px;
  color: ${GATE.muted};

  strong {
    color: ${GATE.ink};
    font-weight: 700;
  }

  img {
    height: 14px;
    width: auto;
    display: block;
  }
`;

const Recipient = styled.div`
  margin-top: 2px;
  font-family: ${GATE.sans};
  font-size: 12px;
  color: ${GATE.muted};
`;

const PlainNote = styled.p`
  margin: 12px 2px 0;
  font-family: ${GATE.sans};
  font-size: 13px;
  line-height: 1.5;
  color: ${GATE.muted};
  text-align: left;
`;

/**
 * Единственная тревожная плашка на экране. Она здесь потому, что цена ошибки необратима: номер
 * выдан под один депозит, и второй перевод на него деньгами клиента уже не станет.
 */
const WarnNote = styled.p`
  margin: 12px 0 0;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #f0b8a8;
  background: #fff4f0;
  font-family: ${GATE.sans};
  font-size: 13px;
  line-height: 1.5;
  color: #b3341f;
  text-align: left;
`;

const HowTo = styled.ol`
  margin: 14px 0 0;
  padding-left: 22px;
  font-family: ${GATE.sans};
  font-size: 14px;
  line-height: 1.5;
  color: ${GATE.ink};
  text-align: left;

  li + li {
    margin-top: 4px;
  }

  li::marker {
    color: ${GATE.muted};
  }
`;

const Disclosures = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;

  details {
    font-family: ${GATE.sans};
    font-size: 13px;
    color: ${GATE.muted};
  }

  summary {
    cursor: pointer;
    padding: 4px 2px;
    color: ${GATE.actionText};
  }

  p {
    margin: 2px 2px 8px;
    line-height: 1.5;
  }
`;

// Место для файла. Пунктир — сигнал «сюда кладут», а не «нажми меня первым»: главная кнопка на экране
// одна. Когда клиент вернулся из банка, рамка становится сплошной и в зоне появляется подсказка —
// ровно там, куда ему теперь смотреть.
const DropZone = styled.button<{ $highlight: boolean }>`
  width: 100%;
  margin-top: 4px;
  padding: 18px 16px;
  box-sizing: border-box;
  border-radius: 14px;
  border: 1.5px ${({ $highlight }) => ($highlight ? 'solid' : 'dashed')}
    ${({ $highlight }) => ($highlight ? GATE.action : 'rgba(106, 170, 58, 0.45)')};
  background: ${({ $highlight }) => ($highlight ? GATE.surface : '#fff')};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-family: ${GATE.sans};
  transition: border-color 0.2s, background 0.2s;

  &:hover {
    border-color: ${GATE.action};
  }
`;

const DropWelcome = styled.span`
  margin-bottom: 4px;
  font-size: 13px;
  line-height: 1.45;
  color: ${GATE.ink};
`;

const DropTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: ${GATE.actionText};
`;

const DropHint = styled.span`
  font-size: 12px;
  color: ${GATE.muted};
`;

const FooterHints = styled.div`
  margin-top: 20px;
  font-family: ${GATE.sans};
  text-align: center;

  p {
    margin: 0;
    font-size: 13px;
    line-height: 1.45;
    color: ${GATE.ink};
  }

  p + p {
    margin-top: 2px;
    font-size: 12px;
    color: ${GATE.muted};
  }
`;

const GatePaymentPage = ({
  transaction,
  uuid,
  timeLeft,
  onCancel,
  onDeclineOffer,
  cancelReasons,
  onLeave,
  receiptIntent,
  onConfirm,
  onConfirmWithReceipt,
}: Props) => {
  const { t } = useTranslation();
  const { requisite } = transaction;
  const isDesktop = useIsDesktop();
  const [showLeave, setShowLeave] = useState(false);
  // Код открыт сразу: на десктопе чек почти всегда лежит в телефоне, и лишний клик здесь стоит
  // ровно тех клиентов, которые не догадались его сделать.
  const [showQr, setShowQr] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [errorText, setErrorText] = useState('');
  // CPAYMENT-1786: a retry in progress is not an error — the client is told to wait, not to act.
  const [retryNotice, setRetryNotice] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { returned } = useReturnedToTab();
  const [amountCopied, setAmountCopied] = useState(false);
  const [requisiteCopied, setRequisiteCopied] = useState(false);
  // Всё, что происходит после нажатия «Выбрать файл», — выбранный файл, ошибка формата, повторная
  // отправка, подсказка «окно не открылось», — появляется внизу экрана, как и кнопка «Я оплатил».
  // На телефоне это ниже края: клиент не видит ни результата, ни следующего шага.
  //
  // Прокрутка мгновенная и сразу, без анимации и без отложенных повторов. Плавная прокрутка,
  // запущенная при открытии окна выбора файла, откладывалась, пока окно открыто, и доигрывала уже
  // после его закрытия — страница ехала под пальцем, и повторное нажатие попадало в раскрывающиеся
  // ссылки выше зоны загрузки. Страница двигается только в момент, когда клиент её не трогает.
  const scrollToBottom = () => {
    if (document.visibilityState === 'hidden') return;
    const doc = document.scrollingElement ?? document.documentElement;
    const maxTop = doc.scrollHeight - window.innerHeight;
    if (window.scrollY >= maxTop - 2) return;
    window.scrollTo?.({ top: doc.scrollHeight, behavior: 'auto' });
  };

  // CPAYMENT-1822: picking a file — the press, the cancel, the empty pick and the silence — lives in
  // one hook shared by every screen that owns its input.
  const picker = useReceiptPicker({
    uuid,
    transaction,
    onFile: setFile,
    onError: (key) => setErrorText(t(key)),
    onBusy: setSending,
    onPickStart: () => {
      setErrorText('');
      setRetryNotice('');
    },
  });

  useLayoutEffect(() => {
    if (file || errorText || retryNotice || picker.helpVisible) scrollToBottom();
    // scrollToBottom reads the DOM at call time; it carries no state worth re-running for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, errorText, retryNotice, picker.helpVisible]);

  // CPAYMENT-1637: у QR-реквизита нет номера для копирования — только ссылка
  // в paymentUrl, по ней рисуем код и кнопку перехода.
  const isQrRequisite = requisite.type === 'qr_code' && !!requisite.paymentUrl;
  const isCard = !!requisite.cardNumber && !requisite.phone;
  // m10 — кошелёк, а не СБП: номер адресует конкретный счёт в приложении, выдан под один депозит
  // и живёт по азербайджанскому формату. Отсюда свой заголовок, свой знак, своя инструкция и
  // предупреждение, которого у остальных телефонных методов нет.
  const isM10 = transaction.paymentMethod === 'M10AZN';
  // Требование чека решает бэкенд — страница только рисует (или не рисует) второй шаг.
  const receiptRequired = transaction.simpleConfirmation === false;
  const showWhyIndividual = !isM10;
  const canConfirm = !sending && (!receiptRequired || !!file);

  // Страницу открыли с телефона по коду, чтобы приложить чек: сразу показываем самый низ экрана —
  // зону загрузки, кнопки и номер заказа.
  useLayoutEffect(() => {
    if (receiptRequired && cameForReceipt()) scrollToBottom();
    // Один раз при показе экрана: дальше страницей управляет клиент.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Полная форма с копейками и знаком валюты: «20 002» и «20 002,00 ₽» клиент переписывает
  // в банк по-разному, и первая форма чаще теряет копейки.
  const currencySign = CURRENCY_SIGNS[requisite.currency] ?? requisite.currency;
  const amountNumber = transaction.amount.toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  // Способ оплаты — словами клиента. Сырой код метода («SBER SBP RUB») ничего ему не говорит и
  // выглядит как техническая ошибка на странице, где он собирается расстаться с деньгами.
  const methodLabel = isQrRequisite
    ? t('gate.methodQr')
    : isCard
      ? t('gate.methodCard')
      : isM10
        ? t('m10.transferByPhone')
        : t('gate.methodSbpPhone');
  const methodIcon = isQrRequisite ? <QrIcon /> : isCard ? <CardIcon /> : isM10 ? <M10Icon /> : <SbpIcon />;
  const requisiteValue = isCard
    ? getCardNumberMask(requisite.cardNumber || '') || requisite.cardNumber || ''
    : isM10
      ? getAzPhoneNumberMask(requisite.phone || '') || requisite.phone || ''
      : getPhoneNumberMask(requisite.phone || '') || requisite.phone || '';
  const requisiteCopyValue = (isCard ? requisite.cardNumber : requisite.phone) || '';

  const method = transaction.paymentMethod?.toUpperCase() ?? '';
  const bankLabel = method.includes('SBER')
    ? 'Сбер'
    : method.includes('ALFA')
      ? 'Альфа'
      : method.includes('TBANK') || method.includes('TINKOFF')
        ? 'Т-Банк'
        : method.includes('OZON')
          ? 'Озон'
          : method.includes('VTB')
            ? 'ВТБ'
            : requisite.acquiringBank || '';

  const bankHasLogo = hasBankLogo(requisite.acquiringBank);

  // Поле по умолчанию — реквизит: это единственная строка, ради которой существует копирование.
  // Сумма передаёт своё явно.
  const copy = (
    value: string,
    done: (v: boolean) => void,
    field: CopiedField = 'requisite',
  ) => {
    copyToClipboard(value.replace(/\s/g, ''), field).then((ok) => {
      if (!ok) return;
      done(true);
      setTimeout(() => done(false), 1500);
    });
  };

  // The receipt and the confirmation are one request. Sending, classifying, retrying and
  // reporting live in services/receiptUpload and TransactionPage; this screen only decides what
  // the client sees.
  const submitReceiptAndConfirm = async (): Promise<void> => {
    if (!file) return;
    setUploadProgress(0);
    setRetryNotice('');

    const result = await onConfirmWithReceipt(file, {
      onProgress: setUploadProgress,
      onRetryScheduled: () => setRetryNotice(t('uploadStatus.retrying')),
    });
    setRetryNotice('');

    if (result.ok) return;

    setUploadProgress(0);
    setErrorText(t(confirmErrorTextKey(result.reason, result.httpStatus)));
  };

  /**
   * «Я оплатил» — единственное завершающее действие экрана: с обязательным чеком файл и
   * подтверждение уходят одним запросом; без чека — только подтверждение. Подтвердить оплату без
   * обязательного чека нельзя — тикет уехал бы оператору с пустыми руками.
   */
  const handlePaidClick = async () => {
    if (!canConfirm) return;
    // Tracked here, before the request — a press that failed must still be in the funnel.
    // `payment.confirm_clicked` fires in TransactionPage once the combined call succeeded.
    track({ name: 'payment.paid_clicked', payload: buildBasePayload(uuid, transaction) });
    setSending(true);
    setErrorText('');
    try {
      if (receiptRequired) {
        await submitReceiptAndConfirm();
        return;
      }
      await onConfirm();
    } finally {
      setSending(false);
    }
  };

  // Короткая строка формата для зоны загрузки, выведенная из той же политики приёма, что и проверка
  // файла: подсказка не может разойтись с тем, что страница реально примет.
  const formatHintKey =
    getReceiptInstructionsKey(transaction.merchant) === 'upload.instructionsPdfOrImage'
      ? 'gate.formatPdfOrImage'
      : 'gate.formatPdf';

  return (
    <GateScreen
      uuid={uuid}
      timeLeft={timeLeft}
      footerSlot={
        <>
          {/* Над главной кнопкой — ровно две короткие строки: что сделать и почему кнопка пока
              неактивна. Без плашек и рамок: здесь уже нечему конкурировать с самой кнопкой. */}
          {receiptRequired && !file && !sending && (
            <FooterHints>
              <p>{t('gate.stepReturn')}</p>
              <p>{t('gate.buttonNeedsFile')}</p>
            </FooterHints>
          )}

          <GateButtonStack>
            <GatePrimaryButton data-sentry-element='onepage-paid' onClick={handlePaidClick} disabled={!canConfirm}>
              {sending ? (
                <>
                  <GateSpinnerRing size={20}>
                    <span style={{ fontSize: 7, color: '#fff' }}>●</span>
                  </GateSpinnerRing>
                  {uploadProgress > 0 && <Percent>{uploadProgress}%</Percent>}
                </>
              ) : (
                t('sbp.iPaid')
              )}
            </GatePrimaryButton>
            <GateQuietButton data-sentry-element='onepage-back' onClick={() => setShowLeave(true)}>{t('gate.cancelAction')}</GateQuietButton>
          </GateButtonStack>

          {showLeave && (
            <GateLeaveModal
              onLeave={onCancel}
              onCancel={() => setShowLeave(false)}
              receiptIntent={receiptIntent}
              onLeaveWithoutCancel={onLeave}
              reasons={cancelReasons}
            />
          )}
        </>
      }
    >
      {/* ─── Шаг 1: куда и сколько ────────────────────────────────────────────
          Номер шага появляется, только когда шагов действительно два: без чека «1» обещал бы
          продолжение, которого не будет. Заголовок — сам способ перевода: его клиент потом ищет в
          приложении банка. */}
      <Section>
        {receiptRequired ? (
          <SectionHead>
            <SectionNum>1</SectionNum>
            <StepTitle>{methodLabel}</StepTitle>
            {/* Знак способа — справа, а не перед текстом: широкий логотип СБП в начале строки сдвинул
                бы заголовок, и заголовки двух шагов перестали бы начинаться на одной линии. */}
            <MethodMark>
              {methodIcon}
            </MethodMark>
          </SectionHead>
        ) : (
          <TypeBadge>
            {methodIcon}
            <span>{methodLabel}</span>
          </TypeBadge>
        )}

        <SectionBody>
          {/* Сумма и реквизит — один объект «сколько и куда», поэтому одна карточка. Сумма первой:
              это крупнейшее число на экране и то, в чём ошибаются чаще всего. */}
          <DataCard>
            <DataLabel>{t('gate.amountTitle')}</DataLabel>
            <AmountValue>
              {amountNumber} <CurrencyMark>{currencySign}</CurrencyMark>
            </AmountValue>
            <AmountCaption>{t('gate.amountExact')}</AmountCaption>
            <CopyTextBtn
              $copied={amountCopied}
              data-sentry-element='onepage-copy-amount'
              onClick={() => copy(`${transaction.amount}`, setAmountCopied, 'amount')}
            >
              {amountCopied ? t('gate.copied') : t('gate.copyAmount')}
            </CopyTextBtn>

            <DataDivider />

            {isQrRequisite ? (
              <QrPayBlock
                url={requisite.paymentUrl!}
                hint={t('qr.hint')}
                action={t('qr.action')}
              />
            ) : (
              <>
                <DataLabel>{isCard ? t('gate.cardLabel') : t('gate.phoneLabel')}</DataLabel>
                <RequisiteValue>{requisiteValue}</RequisiteValue>
                {/* Банк получателя — отдельной строкой и с логотипом: клиент выбирает его в приложении
                    при переводе по СБП, и перепутанный банк — это перевод не туда. */}
                {/* Логотип узнаётся быстрее названия, и рядом с ним название — лишний повтор. Название
                    остаётся только там, где логотипа нет; для экранного диктора оно уходит в alt. */}
                {(bankHasLogo || bankLabel) && (
                  <BankRow>
                    <span>{t('sbp.selectBank')}:</span>
                    {bankHasLogo ? (
                      <BankLogo code={requisite.acquiringBank} alt={bankLabel || requisite.acquiringBank || ''} />
                    ) : (
                      <strong>{bankLabel}</strong>
                    )}
                  </BankRow>
                )}
                {requisite.name && (
                  <Recipient>
                    {t('gate.recipient')}: {requisite.name}
                  </Recipient>
                )}
                <CopyTextBtn
                  $copied={requisiteCopied}
                  data-sentry-element='onepage-copy-requisite'
                  onClick={() => copy(requisiteCopyValue, setRequisiteCopied)}
                >
                  {requisiteCopied ? t('gate.copied') : t('gate.copyNumber')}
                </CopyTextBtn>
              </>
            )}
          </DataCard>

          {showsAmountChangeNotice(transaction) && (
            <GateAmountAlert transaction={transaction} onDecline={() => onDeclineOffer('amount_change')} />
          )}

          {/* Совет, а не предупреждение — поэтому обычный текст, а не цветная плашка. */}
          <PlainNote>{t('gate.bankConfirmNotice')}</PlainNote>

          {isM10 && <WarnNote>{t('m10.warning')}</WarnNote>}

          {/* Три шага без пересказа карточки выше. Про чек сказано до перевода — потом сохранить его
              в приложении банка сложнее. Последний шаг — возврат сюда: без него клиент уходит в банк
              и не знает, что должен вернуться. */}
          <HowTo>
            {isM10 ? (
              <>
                <li>{t('m10.instructionStep1')}</li>
                <li>{t('m10.instructionStep2')}</li>
                <li>{t('m10.instructionStep3')}</li>
              </>
            ) : (
              <li>{t('gate.howToTransfer')}</li>
            )}
            {receiptRequired && <li>{t('gate.receiptNeeded')}</li>}
            {(receiptRequired || !isM10) && (
              <li>{receiptRequired ? t('gate.stepReturn') : t('sbp.instructionStep4')}</li>
            )}
          </HowTo>

          {/* Справка — для тех, у кого возник вопрос. Тихие раскрывающиеся ссылки после инструкции,
              чтобы не разрывать путь «перевести → приложить чек». Перевод частному лицу выглядит
              подозрительно ровно до того момента, пока об этом не сказано прямо — но только там, где
              получателя ждали иным: у кошелька перевод человеку и есть сам способ оплаты, и вопрос
              не возникает. */}
          {(showWhyIndividual || receiptRequired) && (
            <Disclosures>
              {showWhyIndividual && (
                <details>
                  <summary>{t('gate.whyIndividual')}</summary>
                  <p>{t('gate.whyIndividualText')}</p>
                </details>
              )}
              {receiptRequired && (
                <details>
                  <summary>{t('gate.whereIsPdf')}</summary>
                  <p>{t('gate.whereIsPdfSber')}</p>
                  <p>{t('gate.whereIsPdfTbank')}</p>
                </details>
              )}
            </Disclosures>
          )}
        </SectionBody>
      </Section>

      {/* ─── Шаг 2: чек ───────────────────────────────────────────────────── */}
      {receiptRequired && (
        <Section>
          <SectionHead>
            <SectionNum $done={!!file}>2</SectionNum>
            <StepTitle>{t('gate.uploadTitle')}</StepTitle>
          </SectionHead>

          <SectionBody>
            {file ? (
              <FileChip>
                <ClipIcon />
                <FileName>{file.name}</FileName>
                <IconBtn
                  disabled={sending}
                  onClick={() => {
                    if (sending) return;
                    setFile(null);
                  }}
                  data-sentry-element='onepage-file-remove'
                  aria-label={t('modal.cancel')}
                >
                  <RemoveIcon />
                </IconBtn>
              </FileChip>
            ) : (
              <>
                <DropZone
                  type='button'
                  $highlight={returned}
                  onClick={() => {
                    scrollToBottom();
                    picker.open();
                  }}
                  data-sentry-element='onepage-upload-pick'
                >
                  {returned && (
                    <DropWelcome data-sentry-element='onepage-welcome-back'>{t('gate.welcomeBack')}</DropWelcome>
                  )}
                  <DropTitle>
                    <ClipIcon />
                    {t('gate.pickFile')}
                  </DropTitle>
                  <DropHint>{t(formatHintKey)}</DropHint>
                </DropZone>
                {/* CPAYMENT-1822: the dialog gave nothing back twice in a row. Whether it failed to
                    open or the client left it empty-handed, repeating the same tap is what he does
                    next — and it is what the lost deposits look like in the replays. */}
                {picker.helpVisible && <UploadNotice tone='neutral'>{t('upload.pickerHelp')}</UploadNotice>}
              </>
            )}

            {errorText && <UploadNotice tone='error'>{errorText}</UploadNotice>}
            {/* A retry in flight is neutral, not red — the client has nothing to fix and nothing to
                do except wait, and a red block here reads as "it failed again". */}
            {retryNotice && !errorText && <UploadNotice tone='neutral'>{retryNotice}</UploadNotice>}

            {/* Чек чаще всего лежит в телефоне: на компьютере рядом с зоной загрузки сразу открыт код,
                который ведёт на эту же страницу. Оформлен как альтернатива — белый блок с рамкой, а не
                ещё одна карточка данных. */}
            {isDesktop && !file && (
              <>
                <PhoneToggle type='button' data-sentry-element='onepage-open-on-phone' onClick={() => setShowQr((v) => !v)}>
                  <PhoneIcon />
                  {t('upload.openOnPhone')}
                </PhoneToggle>
                {showQr && (
                  <QrPanel>
                    <QrFrame>
                      <QR data={receiptEntryUrl()} size={96} bgColor='#ffffff' fgColor='#111111' />
                    </QrFrame>
                    <QrText>{t('upload.openOnPhoneHint')}</QrText>
                  </QrPanel>
                )}
              </>
            )}
          </SectionBody>
        </Section>
      )}
    </GateScreen>
  );
};

export default GatePaymentPage;
